import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
  styled,
} from "@mui/material";
import { partition } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useState } from "react";
import { Control, useForm } from "react-hook-form";

import {
  UpdateSubscriberSchema,
  UpdateSubscriberSchemaType,
} from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  GetAudienceContactFieldsQuery,
  OptOutDeleteSourceType,
  ResubscribeSubscriberMutation,
  ResubscribeSubscriberMutationVariables,
  SubscriberSource,
  UnsubscribeSubscriberMutation,
  UnsubscribeSubscriberMutationVariables,
  UpdateSubscriberMutation,
  UpdateSubscriberMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink } from "~frontend/components/Link";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfYesNoSelect } from "~frontend/components/forms/react-hook-form-components/RhfYesNoSelect";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import {
  GET_SUBSCRIBER_SUMMARY,
  LIST_SUBSCRIBERS,
  RESUBSCRIBE_SUBSCRIBER,
  UNSUBSCRIBE_SUBSCRIBER,
  UPDATE_SUBSCRIBER,
} from "~frontend/graphql/shortcode";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";

import { Subscriber, useSubscriberLink } from "./subscribersUtils";
import { Shortcode } from "./types";
import { sortContactFields } from "./util";

const SubscriberDialogActions = styled(DialogActions)({
  justifyContent: "space-between",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginBottom: theme.spacing(1),
});

function SubscriberDetails({
  subscriber,
  editing = false,
  control,
  fieldDefinitions,
  showSaveSuccessful,
}: {
  subscriber: Subscriber;
  editing?: boolean;
  control: Control<UpdateSubscriberSchemaType, any, UpdateSubscriberSchemaType>;
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  showSaveSuccessful: boolean;
}) {
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");
  const rows: { message: string; timestamp: number }[] = [];

  // add a row for initial subscription
  switch (subscriber.source) {
    case SubscriberSource.Api:
      rows.push({
        message: "Subscribed via API",
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ShortCodeKeyword:
      rows.push({
        message: `Subscribed via keyword`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.BulkUpload:
      rows.push({
        message: `Subscribed via bulk upload ${subscriber.createdByUser ? `by ${subscriber.createdByUser?.fullName}` : ""}`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.Manual:
      rows.push({
        message: `Subscribed manually ${subscriber.createdByUser ? `by ${subscriber.createdByUser?.fullName}` : ""}`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ActBlueDonation:
      rows.push({
        message: `Subscribed via Act Blue donation`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ActionKitFormSubmission:
      rows.push({
        message: "Subscribed via ActionKit form",
        timestamp: subscriber.createdAt,
      });
      break;
    default:
      subscriber.source satisfies never;
  }

  // Add rows for opt-out and opt-ins
  subscriber.optOuts.forEach(optOut => {
    switch (optOut.source.__typename) {
      case "OptOutAdminListSource":
        rows.push({
          message: `Opted out via bulk upload`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutAdminManualSource":
        rows.push({
          message: `Opted out manually ${optOut.createdByUser ? `by ${optOut.createdByUser?.fullName}` : ""}`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutCampaignAutomaticSource":
        rows.push({
          message: `Opted out automatically via keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutCampaignManualSource":
        rows.push({
          message: `Opted out manually ${optOut.createdByUser ? `by ${optOut.createdByUser?.fullName}` : ""}`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutShortCodeKeywordSource":
        rows.push({
          message: `Opted out via short code keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutShortCodeAutomaticSource":
        rows.push({
          message: `Opted out via automatic opt out keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutSpokeSource":
        rows.push({
          message: `Opted out via Spoke`,
          timestamp: optOut.createdAt,
        });
        break;
      case undefined:
        rows.push({
          message: `Opted out`,
          timestamp: optOut.createdAt,
        });
        break;
      default:
        optOut.source satisfies never;
    }

    const deleteType = optOut.deleteSourceType;
    if (deleteType) {
      switch (deleteType) {
        case OptOutDeleteSourceType.AdminList:
          rows.push({
            message: `Resubscribed via bulk upload`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.AdminManual:
          rows.push({
            message: `Opt out removed manually ${optOut.deletedByUser ? `by ${optOut.deletedByUser?.fullName}` : ""}`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.ApiResubscribe:
          rows.push({
            message: `Resubscribed via API`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.CampaignManual:
          rows.push({
            message: `Opt out removed manually ${optOut.deletedByUser ? `by ${optOut.deletedByUser?.fullName}` : ""}`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.ShortCodeKeyword:
          rows.push({
            message: `Resubscribed via short code keyword`,
            timestamp: optOut.deletedAt,
          });
          break;
        default:
          deleteType satisfies never;
      }
    }
  });

  rows.sort((a, b) => b.timestamp - a.timestamp);

  const [customFields, builtInFields] = partition(
    subscriber.fields,
    f => fieldDefinitions.find(def => def.name === f.name)?.isEditable
  );
  const sortedCustomFields = customFieldsAllowed
    ? sortContactFields(customFields)
    : [];
  const sortedBuiltInFields = sortContactFields(builtInFields);

  return (
    <FlexBox direction="column" gap={3} data-testid="subscriber-details">
      <Box sx={{ backgroundColor: palette.lightestGray, py: 1, px: 2 }}>
        <TwoColumnDisplay
          labelVariant="body2Bold"
          showTopBorder={false}
          showBottomBorder={false}
          hideDividers={editing}
          allRowSx={{
            py: editing ? "3px !important" : "14px !important",
            color: palette.navy,
          }}
          rows={[...sortedBuiltInFields, ...sortedCustomFields].map(
            ({ name, displayName, value, type }) => {
              const key = name;
              const label = displayName || name;

              const content = () => {
                if (editing) {
                  const defaultParams = {
                    fullWidth: true,
                    name: `fields.${name}` as
                      | "subscriberId"
                      | "fields"
                      | `fields.${string}`,
                    control,
                    sx: { mb: 0, pt: 0 },
                  };

                  switch (type) {
                    case AudienceContactFieldType.String:
                    case AudienceContactFieldType.Zip:
                      return <RhfTextField {...defaultParams} />;
                    case AudienceContactFieldType.Number:
                      return <RhfTextField {...defaultParams} />;
                    case AudienceContactFieldType.Boolean:
                      return <RhfYesNoSelect {...defaultParams} />;
                    case AudienceContactFieldType.State:
                      return (
                        <RhfSelect {...defaultParams} withNoneOption>
                          {STATE_MENU_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </RhfSelect>
                      );
                    case AudienceContactFieldType.Enum:
                      return (
                        <RhfSelect {...defaultParams} withNoneOption>
                          {fieldDefinitions
                            .find(f => f.name === label)
                            ?.typeParameters?.values?.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                        </RhfSelect>
                      );
                    default:
                      return null as never;
                  }
                } else {
                  switch (type) {
                    case AudienceContactFieldType.String:
                    case AudienceContactFieldType.State:
                    case AudienceContactFieldType.Zip:
                      return value || "—";

                    case AudienceContactFieldType.Number:
                      return value ? `${Number(value).toLocaleString()}` : "—";

                    case AudienceContactFieldType.Boolean:
                      return value != null ? (value ? "Yes" : "No") : "—";
                    case AudienceContactFieldType.Enum:
                      return value || "—";
                    default:
                      return null as never;
                  }
                }
              };

              const wrappedContent = editing ? (
                content()
              ) : (
                <Typography variant="body2">{content()}</Typography>
              );

              return {
                key,
                label,
                content: wrappedContent,
              };
            }
          )}
        />
      </Box>

      {showSaveSuccessful && (
        <FlexBox
          direction="column"
          alignItems="center"
          sx={{
            padding: "10px 16px 14px 16px",
            alignSelf: "stretch",
            borderRadius: 0.5,
            background: palette.lightestGreen,
          }}
        >
          <Typography variant="body2">
            Subscriber updated successfully.
          </Typography>
        </FlexBox>
      )}

      <Accordion disableGutters sx={{ padding: 0, background: "none" }}>
        <AccordionSummary
          expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">Contact History</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
          }}
        >
          <List sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
            {rows.map(row => (
              <ListItem
                key={row.timestamp}
                sx={{
                  px: 0,
                  "&:not(:last-child)": {
                    borderBottom: `1px solid ${palette.lightGray}`,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2">{row.message}</Typography>
                  }
                  secondary={
                    <Typography variant="body3">
                      {formatShortDateTime(DateTime.fromMillis(row.timestamp))}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </FlexBox>
  );
}

export default function SubscriberDialog({
  subscriber,
  updateSubscriber,
  shortcode,
  audienceContactFields,
  onClose,
}: {
  subscriber: Subscriber;
  updateSubscriber: (subscriber: Subscriber) => void;
  shortcode: Shortcode;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const subscriberLink = useSubscriberLink();
  const formatErrorMessage = useErrorMessageFormatter();
  const [dialogPage, setDialogPage] = useState<"details" | "optout" | "optin">(
    "details"
  );
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [showSaveSuccessful, setShowSaveSuccessful] = useState(false);

  const [editing, setEditing] = useState(false);

  const initialValues = UpdateSubscriberSchema(audienceContactFields).parse({
    subscriberId: subscriber.id,
    fields: (subscriber.fields || []).reduce(
      (acc, field) => {
        if (field.type === AudienceContactFieldType.Boolean) {
          acc[field.name] = field.value != null ? field.value : null;
        } else {
          acc[field.name] = field.value || null;
        }
        return acc;
      },
      {} as Record<string, string | boolean | null>
    ),
  });

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: initialValues,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [updateSubscriberMutation] = useMutation<
    UpdateSubscriberMutation,
    UpdateSubscriberMutationVariables
  >(UPDATE_SUBSCRIBER, {
    refetchQueries: [GET_SUBSCRIBER_SUMMARY, LIST_SUBSCRIBERS],
    awaitRefetchQueries: true,
  });

  const [optOut] = useMutation<
    UnsubscribeSubscriberMutation,
    UnsubscribeSubscriberMutationVariables
  >(UNSUBSCRIBE_SUBSCRIBER, {
    variables: {
      input: {
        subscriberId: subscriber.id,
      },
    },
    refetchQueries: [GET_SUBSCRIBER_SUMMARY, LIST_SUBSCRIBERS],
    awaitRefetchQueries: true,
  });

  const [optIn] = useMutation<
    ResubscribeSubscriberMutation,
    ResubscribeSubscriberMutationVariables
  >(RESUBSCRIBE_SUBSCRIBER, {
    variables: {
      input: {
        subscriberId: subscriber.id,
      },
    },
    refetchQueries: [GET_SUBSCRIBER_SUMMARY, LIST_SUBSCRIBERS],
    awaitRefetchQueries: true,
  });

  const handleOptOut = async () => {
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await optOut();
    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      setDialogPage("details");
      updateSubscriber(data!.unsubscribeSubscriber);
    }

    setMutationLoading(false);
  };

  const handleOptIn = async () => {
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await optIn();
    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      setDialogPage("details");
      updateSubscriber(data!.resubscribeSubscriber);
    }

    setMutationLoading(false);
  };

  const handleUpdateSubmit = async (rawValues: unknown) => {
    const values = UpdateSubscriberSchema(audienceContactFields).parse(
      rawValues
    );
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await updateSubscriberMutation({
      variables: {
        input: {
          subscriberId: values.subscriberId,
          fields: JSON.stringify(values.fields),
        },
      },
    });

    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      updateSubscriber(data!.updateSubscriber);
      setEditing(false);
      setShowSaveSuccessful(true);
    }

    setMutationLoading(false);
  };

  function renderDetails() {
    return (
      <>
        <DialogContent sx={{ px: 5 }}>
          {editing && <RhfErrorBanner control={control} />}
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <SubscriberDetails
            subscriber={subscriber}
            editing={editing}
            control={control}
            fieldDefinitions={audienceContactFields}
            showSaveSuccessful={showSaveSuccessful}
          />
        </DialogContent>
        <SubscriberDialogActions sx={{ px: 5 }}>
          {subscriber.optOuts.find(o => !o.deletedAt) ? (
            <Button
              onClick={() => setDialogPage("optin")}
              color="primary"
              variant="outlined"
            >
              Opt In
            </Button>
          ) : (
            <Button
              onClick={() => setDialogPage("optout")}
              color="error"
              variant="outlined"
            >
              Opt Out
            </Button>
          )}
          <FlexBox gap={0.5}>
            <Button onClick={onClose} variant="contained" color="tertiary">
              Close
            </Button>
            <ButtonLink
              to={subscriberLink(subscriber)}
              color="tertiary"
              variant="contained"
            >
              View Thread
            </ButtonLink>
            {editing ? (
              <>
                <Button
                  onClick={handleSubmit(handleUpdateSubmit)}
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || mutationLoading}
                >
                  {isSubmitting || mutationLoading ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setShowSaveSuccessful(false);
                  setEditing(true);
                }}
                color="primary"
                variant="contained"
              >
                Edit
              </Button>
            )}
          </FlexBox>
        </SubscriberDialogActions>
      </>
    );
  }

  function renderOptOut() {
    return (
      <>
        <DialogContent>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <Typography>
            Are you sure you want to unsubscribe this contact? They will be sent
            your short code's opt out message: "
            {shortcode.shortCodeConfig?.optOutMessage}"
          </Typography>
        </DialogContent>
        <SubscriberDialogActions>
          <Button
            onClick={() => {
              setDialogPage("details");
              setMutationError(null);
            }}
            disabled={mutationLoading}
            color="tertiary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOptOut}
            color="error"
            disabled={mutationLoading}
            variant="outlined"
          >
            {mutationLoading ? "Opting Out..." : "Opt Out"}
          </Button>
        </SubscriberDialogActions>
      </>
    );
  }

  function renderOptIn() {
    return (
      <>
        <DialogContent>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <Typography>
            Are you sure you want to resubscribe this contact? They will be sent
            your short code's welcome message: "
            {shortcode.shortCodeConfig?.optInMessage}"
          </Typography>
        </DialogContent>
        <SubscriberDialogActions>
          <Button
            onClick={() => {
              setDialogPage("details");
              setMutationError(null);
            }}
            disabled={mutationLoading}
            color="tertiary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOptIn}
            color="primary"
            variant="contained"
            disabled={mutationLoading}
          >
            {mutationLoading ? "Opting In..." : "Opt In"}
          </Button>
        </SubscriberDialogActions>
      </>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 5 }}>
        Subscriber Details for{" "}
        <strong>{prettyPhoneNumber(subscriber.phoneNumber)}</strong>
      </DialogTitle>
      {dialogPage === "details" && renderDetails()}
      {dialogPage === "optout" && renderOptOut()}
      {dialogPage === "optin" && renderOptIn()}
    </Dialog>
  );
}
